import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { showSuccess } from '../components/GlobalSnackbar';
import { formatFen } from '../utils/money';

const PAYMENT_METHODS = [
  { value: 'cash', label: '现金' },
  { value: 'wechat', label: '微信' },
  { value: 'alipay', label: '支付宝' },
  { value: 'bank', label: '银行转账' },
  { value: 'other', label: '其他' },
];

const PAYMENT_STAGES = [
  { value: 'advance', label: '首付款' },
  { value: 'progress', label: '进度款' },
  { value: 'final', label: '尾款' },
  { value: 'warranty', label: '质保金' },
  { value: 'other', label: '其他' },
];

export default function IncomeList() {
  const [incomes, setIncomes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [newIncome, setNewIncome] = useState({
    projectId: '',
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    paymentMethod: 'bank',
    stage: 'progress',
    description: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [incomesRes, projectsRes] = await Promise.all([
        api.get('/api/incomes'),
        api.get('/api/projects?pageSize=500'),
      ]);
      setIncomes(incomesRes.data.data || incomesRes.data);
      setProjects(projectsRes.data.data || projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || error.message || '加载收款记录失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreate = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const income = {
          id: Date.now().toString(),
          ...newIncome,
          amount: Math.round(parseFloat(newIncome.amount || 0) * 100),
        };
        await api.post('/api/incomes', income);
        setOpenDialog(false);
        resetForm();
        showSuccess('收款已记录');
        fetchData();
      } catch (error) {
        console.error('Error creating income:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (item) => {
    setEditingItem(item);
    setNewIncome({
      projectId: item.projectId || '',
      date: item.date || '',
      amount: item.amount ? String(item.amount / 100) : '',
      paymentMethod: item.paymentMethod || 'bank',
      stage: item.stage || 'progress',
      description: item.description || '',
      notes: item.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const data = {
          ...newIncome,
          amount: Math.round(parseFloat(newIncome.amount || 0) * 100),
        };
        await api.put(`/api/incomes/${editingItem.id}`, data);
        setOpenDialog(false);
        setEditingItem(null);
        resetForm();
        showSuccess('收款已更新');
        fetchData();
      } catch (error) {
        console.error('Error updating income:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/incomes/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchData();
      showSuccess('收款记录已删除');
    } catch (error) {
      console.error('Error deleting income:', error);
    }
  };

  const resetForm = () => {
    setNewIncome({
      projectId: '',
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      paymentMethod: 'bank',
      stage: 'progress',
      description: '',
      notes: '',
    });
  };


  const getPaymentMethodText = (method) => {
    return PAYMENT_METHODS.find(m => m.value === method)?.label || method;
  };

  const getPaymentStageText = (stage) => {
    return PAYMENT_STAGES.find(s => s.value === stage)?.label || stage;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>收款管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchData}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">收款管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingItem(null); resetForm(); setOpenDialog(true); }}
        >
          新增收款
        </Button>
      </Box>

      {incomes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无收款记录
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日期</TableCell>
                <TableCell>项目</TableCell>
                <TableCell>收款方式</TableCell>
                <TableCell>收款阶段</TableCell>
                <TableCell>说明</TableCell>
                <TableCell align="right">金额</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {incomes.map((income) => (
                <TableRow key={income.id}>
                  <TableCell>{income.date}</TableCell>
                  <TableCell>{income.projectName || '-'}</TableCell>
                  <TableCell>{getPaymentMethodText(income.paymentMethod)}</TableCell>
                  <TableCell>{getPaymentStageText(income.stage)}</TableCell>
                  <TableCell>{income.description || '-'}</TableCell>
                  <TableCell align="right">{formatFen(income.amount)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditStart(income)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(income.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingItem(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '编辑收款' : '新增收款'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="项目"
            fullWidth
            variant="outlined"
            value={newIncome.projectId}
            onChange={(e) => setNewIncome({ ...newIncome, projectId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="日期"
            fullWidth
            variant="outlined"
            type="date"
            value={newIncome.date}
            onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="金额（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newIncome.amount}
            onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="收款方式"
            fullWidth
            variant="outlined"
            value={newIncome.paymentMethod}
            onChange={(e) => setNewIncome({ ...newIncome, paymentMethod: e.target.value })}
            sx={{ mb: 2 }}
          >
            {PAYMENT_METHODS.map((method) => (
              <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="收款阶段"
            fullWidth
            variant="outlined"
            value={newIncome.stage}
            onChange={(e) => setNewIncome({ ...newIncome, stage: e.target.value })}
            sx={{ mb: 2 }}
          >
            {PAYMENT_STAGES.map((stage) => (
              <MenuItem key={stage.value} value={stage.value}>{stage.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="说明"
            fullWidth
            variant="outlined"
            value={newIncome.description}
            onChange={(e) => setNewIncome({ ...newIncome, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newIncome.notes}
            onChange={(e) => setNewIncome({ ...newIncome, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingItem(null); }}>取消</Button>
          <Button onClick={editingItem ? handleEditSave : handleCreate} variant="contained" disabled={!newIncome.projectId || !(parseFloat(newIncome.amount) > 0)}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这条收款记录吗？"
      />
    </Box>
  );
}
